import React from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import classNames from 'classnames';

import DateRangeIcon from '@material-ui/icons/DateRange';
import { useMediaQuery } from '@material-ui/core';

import FormHelperText from '@material-ui/core/FormHelperText';
import { Button, Typography } from '@material-ui/core';

import Checkbox from '@material-ui/core/Checkbox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import FormGroup from '@material-ui/core/FormGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';

import { DateRangePicker } from 'react-dates';
import { CalendarDay, CalendarMonthCaption } from './styles';
import { LOCALES_OBJ } from 'constants/base';

import { useStyles } from './styles';
import { getIsHalf } from '../../utils/book-accommodation/getIsHalf';
import uuid from 'uuid/v1';

const CustomDateRangePicker = ({
  id,
  isStartDateRenderMonth,
  input,
  minimumNights,
  availableRange,
  disabledDates,
  changeMonthsCallback,
  label,
  className,
  containerClassName,
  initialFocus,
  dateRangePickerClass,
  inputClassName,
  isOutsideRange,
  numberOfMonths,
  isHideDatesLabel,
  labelTypographyVariant,
  daySize,
  secondLabelClass,
  meta: { touched, error, submitFailed },
  widget,
  updateBooking,
  statistics,
  accomodationDialog
}) => {
  const classes = useStyles();
  const [t, i18n] = useTranslation();

  const selectedLanguage = i18n.language;

  const endDate = availableRange ? availableRange.endDate : null;
  const endDatePlusOneDay = endDate ? moment(endDate).add(1, 'days') : null;
  const startDateFocusId = `startDate-${id || uuid(20)}`;

  const [focus, setFocus] = React.useState(initialFocus || null);
  const [isHiddenNextButton, setHiddenNextButton] = React.useState(false);
  const [firstDisabledDate, setFirstDisabledDate] = React.useState(null);

  const [render, setRender] = React.useState(true);
  const [renderMonth, setRenderMonth] = React.useState(
    isStartDateRenderMonth && input.value.startDate
      ? input.value.startDate
      : moment()
  );

  React.useEffect(() => {
    setRender(!render);
    setTimeout(() => {
      setRender(render);
    }, 0);
  }, [selectedLanguage]);

  let firstDisabledDateAfterSelectedDate = firstDisabledDate;

  const selectedStartDate = input.value.startDate;
  const selectedEndDate = input.value.endDate;

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));

  const onChangeHandler = (startDate, endDate) => {
    setFirstDisabledDate(null);

    if (!moment(startDate).isSame(selectedStartDate, 'day')) {
      input.onChange({ startDate, endDate: null });
      return;
    }

    input.onChange({ startDate, endDate });
  };

  const onPrevMonthClickHandler = date => {
    setRenderMonth(date);
    setHiddenNextButton(false);
    if (changeMonthsCallback) changeMonthsCallback(date);
  };

  const onNextMonthClickHandler = date => {
    setRenderMonth(date);
    const currentDate = moment();
    const endAvailableDate = moment(currentDate).add(23, 'M');

    const isTheSameDates = moment(date)
      .startOf('month')
      .isSame(endAvailableDate.startOf('month'));

    if (isTheSameDates) setHiddenNextButton(true);
    if (changeMonthsCallback) changeMonthsCallback(date);
  };

  const isBlockedDayByDisabledDates = (disabledDates, date) => {
    return disabledDates.find(item => {
      const itemDate = moment(item.date, 'YYYY-MM-DD');
      const isSameDate = moment(itemDate).isSame(date, 'day');

      if (isSameDate && item.deactivationBooking) return true;

      if (selectedStartDate && !selectedEndDate) {
        const isSameOrAfterDate = moment(date).isSameOrAfter(selectedStartDate);

        if (!item.isAvailableForCheckOut && isSameDate && isSameOrAfterDate) {
          return true;
        }
      }

      if (isSameDate) {
        const checkinCheckoutDate = disabledDates.filter(item =>
          moment(item.date, 'YYYY-MM-DD').isSame(itemDate, 'day')
        );
        if (checkinCheckoutDate.length > 1) {
          return true;
        }

        if (availableRange?.startDate) {
          if (
            moment(item.date, 'YYYY-MM-DD').isSame(
              availableRange.startDate,
              'day'
            ) ||
            moment(item.date, 'YYYY-MM-DD').isSame(
              availableRange.endDate,
              'day'
            ) ||
            moment(item.date, 'YYYY-MM-DD').isSame(moment(), 'day')
          ) {
            return true;
          }
        }
      }

      return (
        !item.isAvailableForCheckIn &&
        !item.isAvailableForCheckOut &&
        isSameDate
      );
    });
  };

  const isBlockedDay = date => {
    if (disabledDates) {
      const isDisabled = isBlockedDayByDisabledDates(disabledDates, date);
      if (isDisabled) {
        return true;
      }
    }

    if (!availableRange) return false;

    if (availableRange.endDate && availableRange.endDate !== 'None') {
      const isBetween = moment(date).isBetween(
        availableRange.startDate,
        availableRange.endDate,
        'day',
        '[]'
      );

      if (isBetween) {
        if (!availableRange.daysOfWeek.length) return false;
        const dayOfDate = moment(date).format('ddd');
        return !availableRange.daysOfWeek.includes(dayOfDate);
      }
    } else {
      const isSameOrAfter = moment(date).isSameOrAfter(
        availableRange.startDate,
        'day'
      );

      if (isSameOrAfter) {
        if (!availableRange.daysOfWeek.length) return false;
        const dayOfDate = moment(date).format('ddd');
        return !availableRange.daysOfWeek.includes(dayOfDate);
      }
    }

    return true;
  };

  const isBlockedDayAfterSelectedStartDate = (date, isDayBlocked) => {
    if (selectedStartDate && !selectedEndDate) {
      if (isDayBlocked && moment(date).isAfter(selectedStartDate, 'day')) {
        if (
          endDatePlusOneDay &&
          moment(date).isSameOrAfter(endDatePlusOneDay, 'day')
        ) {
          return isDayBlocked;
        }
        const isTheSameDisabledDate = firstDisabledDateAfterSelectedDate
          ? moment(firstDisabledDateAfterSelectedDate).isSame(date, 'day')
          : null;
        if (!firstDisabledDateAfterSelectedDate || !isTheSameDisabledDate) {
          firstDisabledDateAfterSelectedDate = date;
          setFirstDisabledDate(date);
        }
        return true;
      }

      if (
        firstDisabledDateAfterSelectedDate &&
        moment(date).isSameOrAfter(firstDisabledDateAfterSelectedDate, 'day')
      ) {
        return true;
      }
    } else if (firstDisabledDateAfterSelectedDate) {
      setFirstDisabledDate(null);
    }

    return isDayBlocked;
  };

  const [checked, setChecked] = React.useState(true);

  const checkboxChangeHandler = () => {
    setChecked(!checked);
    input.onChange({ startDate: moment(), endDate: null });
  };

  const handleResetDate = () => {
    onChangeHandler(null, null);
    setFocus(initialFocus || null);
  };

  const [isDisabledDates, setIsDisabledDates] = React.useState(false);
  const [disabledDateslist, setDisabledDateslist] = React.useState([]);

  React.useEffect(() => {
    const startDate = moment(renderMonth).startOf('month');
    const endDate = moment(renderMonth)
      .add(2, 'month')
      .endOf('month');

    const dates = [];
    const isBlocked = isBlockedDay(startDate);

    if (isBlocked) dates.push(startDate.clone());

    while (startDate.add(1, 'days').diff(endDate) < 0) {
      const isBlocked = isBlockedDay(startDate);
      if (isBlocked) dates.push(startDate.clone());
    }

    setDisabledDateslist(dates);
    setIsDisabledDates(true);
  }, [availableRange, disabledDates, renderMonth]);

  if (!isDisabledDates) return null;

  return (
    <div className={classNames(classes.container, containerClassName)}>
      <div className={classNames(classes.wrapper, className)}>
        {initialFocus ? (
          <label className={classNames(classes.secondLabel, secondLabelClass)}>
            <Typography
              variant={labelTypographyVariant ? labelTypographyVariant : 'h2'}
            >
              {t(label)}
            </Typography>

            <div className={classes.datesLabel}>
              <Typography variant="body1" className={classes.fromTo}>
                {input.value && !isHideDatesLabel ? `${t('From – To')}:` : ' '}
              </Typography>

              <Typography variant="body1">
                {input.value && (
                  <>
                    {input.value.startDate &&
                      moment(input.value.startDate)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM D, YYYY')}{' '}
                    –{' '}
                    {input.value.endDate &&
                      moment(input.value.endDate)
                        .locale(LOCALES_OBJ[selectedLanguage])
                        .format('MMM D, YYYY')}
                  </>
                )}
              </Typography>
            </div>
          </label>
        ) : (
          <label htmlFor={startDateFocusId} className={classes.label}>
            {t(label)}
          </label>
        )}

        <label
          htmlFor={startDateFocusId}
          onClick={() => setFocus(startDateFocusId)}
          className={classNames(
            classes.customInputDate,
            {
              [classes.focus]: focus
            },
            {
              [classes.errorInput]: (touched || submitFailed) && error
            },
            inputClassName
          )}
        >
          <DateRangeIcon className={classes.calendarIcon} />
          {input.value.startDate ? (
            moment(input.value.startDate)
              .locale(LOCALES_OBJ[selectedLanguage])
              .format('MMM D, YYYY')
          ) : (
            <div>{t('Start date')}</div>
          )}
          <span className={classes.dateRangeDivider}>-</span>
          {input.value.endDate ? (
            moment(input.value.endDate)
              .locale(LOCALES_OBJ[selectedLanguage])
              .format('MMM D, YYYY')
          ) : (
            <div>{t('End date')}</div>
          )}
        </label>

        {!initialFocus && (touched || submitFailed) && error && (
          <FormHelperText className={classes.error} error>
            {typeof error === 'object'
              ? t(error.text, error.variables)
              : t(error)}
          </FormHelperText>
        )}

        <div
          className={classNames(
            classes.hiddenDateRangePicker,
            dateRangePickerClass
          )}
          style={{ zIndex: 10 }}
        >
          {render && (
            <DateRangePicker
              verticalSpacing={0}
              noBorder={true}
              customArrowIcon={<div>{''}</div>}
              readOnly={true}
              startDateId={startDateFocusId}
              endDateId="endDate"
              displayFormat="MMM D, YYYY"
              startDate={input.value.startDate}
              endDate={input.value.endDate}
              onDatesChange={({ startDate, endDate }) => {
                onChangeHandler(startDate, endDate);
              }}
              onPrevMonthClick={onPrevMonthClickHandler}
              onNextMonthClick={onNextMonthClickHandler}
              minimumNights={minimumNights}
              navNext={isHiddenNextButton ? <div>{''}</div> : ''}
              focusedInput={focus}
              onFocusChange={focusedInput => {
                setFocus(focusedInput || initialFocus || null);
              }}
              isOutsideRange={isOutsideRange}
              initialVisibleMonth={() => renderMonth}
              isDayBlocked={date => {
                return !!disabledDateslist.some(disabledDate => {
                  const isBlocked = disabledDate.isSame(date, 'day');
                  return isBlockedDayAfterSelectedStartDate(date, isBlocked);
                });
              }}
              numberOfMonths={numberOfMonths ? numberOfMonths : 2}
              daySize={daySize ? daySize : 38}
              // numberOfMonths={isSmallScreen ? 1 : 2}
              // daySize={isSmallScreen ? 30 : 35}
              renderMonthElement={({ month }) =>
                moment(month)
                  .locale(LOCALES_OBJ[selectedLanguage])
                  .format('MMMM YYYY')
              }
              renderDayContents={day => {
                if (selectedLanguage === 'Norway') {
                  day._locale._weekdaysMin = [
                    'Sø',
                    'Ma',
                    'Ti',
                    'On',
                    'To',
                    'Fr',
                    'Lø'
                  ];
                } else {
                  day._locale._weekdaysMin = [
                    'Su',
                    'Mo',
                    'Tu',
                    'We',
                    'Th',
                    'Fr',
                    'Sa'
                  ];
                }

                const { disableLeft, disableRight } = getIsHalf(
                  disabledDates,
                  day
                );

                return (
                  <div
                    className={classNames({
                      [classes.dayHalfAvailable]: disableLeft || disableRight,
                      [classes.dayLeftDisable]: disableLeft,
                      [classes.dayRightDisable]: disableRight
                    })}
                  >
                    <div>{day.format('D')}</div>
                  </div>
                );
              }}
              hideKeyboardShortcutsPanel={true}
            />
          )}
          {initialFocus && input.value.startDate && (
            <Button
              className={classes.ClearButton}
              onClick={() => {
                handleResetDate();
              }}
            >
              Clear date
            </Button>
          )}
        </div>
        {initialFocus && (touched || submitFailed) && error && (
          <FormHelperText className={classes.errorOpenedPicker} error>
            {typeof error === 'object'
              ? t(error.text, error.variables)
              : t(error)}
          </FormHelperText>
        )}
      </div>

      {!widget && !updateBooking && !statistics && !accomodationDialog && (
        <FormGroup aria-label="position">
          <FormControlLabel
            label={t('Available all the time')}
            labelPlacement="end"
            control={
              <Checkbox
                icon={
                  <CheckBoxOutlineBlankIcon className={classes.checkboxIcon} />
                }
                checkedIcon={<CheckBoxIcon className={classes.checkboxIcon} />}
                checked={!input.value.endDate}
                onChange={checkboxChangeHandler}
                disabled={!input.value.endDate}
                color="default"
                classes={{
                  root: classes.checkboxRoot,
                  checked: classes.checked,
                  disabled: classes.disabledCheckbox
                }}
              />
            }
          />
        </FormGroup>
      )}
    </div>
  );
};

export default CustomDateRangePicker;
